/**
 * アップロード永続化サービス
 * IndexedDBを使用してアップロード状態を保存・復元
 */

export interface PersistedUpload {
  sessionId: string;
  fileName: string;
  fileSize: number;
  fileHash?: string;
  totalChunks: number;
  uploadedChunks: number[];
  metadata: {
    title: string;
    description: string;
    visibility: string;
  };
  createdAt: number;
  lastUpdatedAt: number;
}

class UploadPersistenceService {
  private dbName = 'MedTubeUploads';
  private storeName = 'uploads';
  private version = 1;
  private db: IDBDatabase | null = null;

  /**
   * IndexedDB初期化
   */
  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        console.error('IndexedDB初期化エラー:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // オブジェクトストアが存在しない場合は作成
        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, { keyPath: 'sessionId' });

          // インデックスを作成
          objectStore.createIndex('fileName', 'fileName', { unique: false });
          objectStore.createIndex('createdAt', 'createdAt', { unique: false });
          objectStore.createIndex('lastUpdatedAt', 'lastUpdatedAt', { unique: false });
        }
      };
    });
  }

  /**
   * データベースを取得（未初期化の場合は初期化）
   */
  private async getDb(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init();
    }
    return this.db!;
  }

  /**
   * セッションを保存
   */
  async saveSession(session: PersistedUpload): Promise<void> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const objectStore = transaction.objectStore(this.storeName);

      const request = objectStore.put(session);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error('セッション保存エラー:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * セッションを取得
   */
  async getSession(sessionId: string): Promise<PersistedUpload | null> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly');
      const objectStore = transaction.objectStore(this.storeName);

      const request = objectStore.get(sessionId);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        console.error('セッション取得エラー:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * 全セッションを取得
   */
  async getAllSessions(): Promise<PersistedUpload[]> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly');
      const objectStore = transaction.objectStore(this.storeName);

      const request = objectStore.getAll();

      request.onsuccess = () => {
        // 更新日時でソート（新しいものが先）
        const sessions = (request.result || []) as PersistedUpload[];
        sessions.sort((a, b) => b.lastUpdatedAt - a.lastUpdatedAt);
        resolve(sessions);
      };

      request.onerror = () => {
        console.error('全セッション取得エラー:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * セッションを削除
   */
  async deleteSession(sessionId: string): Promise<void> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const objectStore = transaction.objectStore(this.storeName);

      const request = objectStore.delete(sessionId);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error('セッション削除エラー:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * 進捗を更新
   */
  async updateProgress(sessionId: string, uploadedChunks: number[]): Promise<void> {
    const session = await this.getSession(sessionId);

    if (!session) {
      console.warn('セッションが見つかりません:', sessionId);
      return;
    }

    session.uploadedChunks = uploadedChunks;
    session.lastUpdatedAt = Date.now();

    await this.saveSession(session);
  }

  /**
   * 古いセッションをクリーンアップ（7日以上前）
   */
  async cleanupOldSessions(): Promise<void> {
    const sessions = await this.getAllSessions();
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

    const deletePromises = sessions
      .filter((session) => session.createdAt < sevenDaysAgo)
      .map((session) => this.deleteSession(session.sessionId));

    await Promise.all(deletePromises);
  }

  /**
   * ファイル名とサイズで既存セッションを検索
   */
  async findSessionByFile(fileName: string, fileSize: number): Promise<PersistedUpload | null> {
    const sessions = await this.getAllSessions();

    const matchingSession = sessions.find(
      (session) => session.fileName === fileName && session.fileSize === fileSize
    );

    return matchingSession || null;
  }

  /**
   * データベースをクリア（開発/デバッグ用）
   */
  async clearAll(): Promise<void> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const objectStore = transaction.objectStore(this.storeName);

      const request = objectStore.clear();

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error('クリアエラー:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * データベースを閉じる
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

// シングルトンインスタンス
export const uploadPersistenceService = new UploadPersistenceService();

// デフォルトエクスポート
export default uploadPersistenceService;
